/** Formatting helpers. All dates render in the visitor's locale. */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * A bare `YYYY-MM-DD` — which is what the "Recorded on" date input produces —
 * is parsed as *UTC* midnight per spec, then rendered in local time. Anywhere
 * west of Greenwich that lands on the previous day, so a recording dated the
 * 22nd displayed as the 21st. Build those in local time instead; timestamps
 * with a time component are already unambiguous.
 */
function toDate(value: string) {
  const match = DATE_ONLY.exec(value)
  return match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value)
}

export function formatDate(iso: string | null | undefined, style: 'long' | 'short' = 'long') {
  if (!iso) return 'Undated'
  const date = toDate(iso)
  if (Number.isNaN(date.getTime())) return 'Undated'
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: style === 'long' ? 'long' : 'short',
    day: 'numeric',
  })
}

export function formatRelative(iso: string | null | undefined) {
  if (!iso) return 'never'
  const then = toDate(iso).getTime()
  if (Number.isNaN(then)) return 'never'

  const seconds = Math.round((then - Date.now()) / 1000)
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3600],
    ['minute', 60],
  ]

  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return formatter.format(Math.round(seconds / size), unit)
  }
  return formatter.format(Math.round(seconds), 'second')
}

/** Clock time for the player: 4:07, or 1:04:07 past an hour. */
export function formatDuration(seconds: number | null | undefined) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

/** Human phrasing for lists — "12 min listen". */
export function formatLength(seconds: number | null | undefined) {
  const total = Math.round(Number(seconds) || 0)
  if (!total) return null
  if (total < 60) return `${total} sec`
  const minutes = Math.round(total / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours}h ${rest}m` : `${hours}h`
}

export function formatBytes(bytes: number | null | undefined) {
  const value = Number(bytes) || 0
  if (value < 1024) return `${value} B`
  const units = ['KB', 'MB', 'GB']
  let size = value / 1024
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${size < 10 ? size.toFixed(1) : Math.round(size)} ${units[unit]}`
}

export const formatNumber = (value: number) => new Intl.NumberFormat().format(value ?? 0)

/** "1 read" / "2 reads" — because a brand-new theory showing "1 reads" is scruffy. */
export const plural = (count: number, one: string, many = `${one}s`) =>
  `${formatNumber(count)} ${count === 1 ? one : many}`

/** The joke rating scale attached to every theory. */
export function credibilityLabel(score: number) {
  if (score >= 90) return 'Basically proven'
  if (score >= 70) return 'Suspiciously plausible'
  if (score >= 50) return 'Could go either way'
  if (score >= 30) return 'Structurally unsound'
  if (score >= 12) return 'Deeply unwell'
  return 'Actively dangerous'
}

export function slugPreview(title: string) {
  return title
    .normalize('NFKD')
    .replaceAll(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replaceAll(/['’]/g, '')
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-+|-+$/g, '')
    .slice(0, 80)
}
